import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, BarChart3, Users, FileText, HardDrive, CalendarCheck, AlertCircle, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";

function formatBytes(bytes = 0) {
  if (!bytes) return "0 MB";
  const mb = bytes / 1024 / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

function formatDate(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("es-ES");
  } catch {
    return v;
  }
}

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs font-black uppercase tracking-widest text-slate-500">{label}</div>
          <div className="mt-2 text-3xl font-black text-slate-900">{value}</div>
          {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
        </div>
        <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[#4d7c0f]/10 text-[#4d7c0f]">
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </div>
  );
}

export default function AdminStats() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [statsResp, ordersResp] = await Promise.all([
      api.get("/admin/history/stats"),
      api.get("/admin/history/orders"),
    ]);
    setStats(statsResp.data);
    setOrders((ordersResp.data || []).slice(0, 25));
    setLoading(false);
  };

  useEffect(() => {
    load().catch(() => setLoading(false));
  }, []);

  return (
    <div>
      <div className="mb-6 flex items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-black text-slate-900">Estadísticas</h1>
          <p className="text-sm text-slate-600">
            Resumen histórico de uso de la App KAPEI. No guarda archivos, solo datos útiles.
          </p>
        </div>
        <Button variant="outline" onClick={load} className="gap-2">
          <RefreshCw className="h-4 w-4" /> Recargar
        </Button>
      </div>

      {loading && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-slate-500">
          Cargando estadísticas…
        </div>
      )}

      {!loading && (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <StatCard icon={BarChart3} label="Pedidos históricos" value={stats?.orders || 0} hint="Total registrado" />
            <StatCard icon={CalendarCheck} label="Pedidos hoy" value={stats?.orders_today || 0} hint="Desde medianoche" />
            <StatCard icon={CalendarCheck} label="Pedidos este mes" value={stats?.orders_this_month || 0} hint="Mes actual" />
            <StatCard icon={Users} label="Clientes únicos" value={stats?.unique_clients || 0} hint="Por email registrado" />
            <StatCard icon={Users} label="Clientes nuevos" value={stats?.new_clients_this_month || 0} hint="Primer pedido este mes" />
            <StatCard icon={FileText} label="Páginas" value={stats?.pages || 0} hint="Total detectado" />
            <StatCard icon={HardDrive} label="Datos recibidos" value={formatBytes(stats?.total_size || 0)} hint="Solo referencia" />
            <StatCard icon={AlertCircle} label="Incidencias" value={stats?.incident_orders || 0} hint="Pedidos en incidencia" />
          </div>

          <div className="mt-8 grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h2 className="mb-3 flex items-center gap-2 text-lg font-black text-slate-900">
                <Users className="h-5 w-5 text-[#4d7c0f]" /> Top clientes
              </h2>
              <div className="space-y-2">
                {(stats?.top_clients || []).length === 0 && (
                  <div className="text-sm text-slate-500">Aún no hay suficientes datos.</div>
                )}
                {(stats?.top_clients || []).map((c, i) => (
                  <div key={c._id} className="flex items-center justify-between rounded-lg bg-slate-50 p-3 text-sm">
                    <div>
                      <div className="font-black text-slate-900">{i + 1}. {c.name || c._id}</div>
                      <div className="text-xs text-slate-500">{c._id}</div>
                    </div>
                    <div className="text-right text-xs">
                      <div><strong>{c.orders}</strong> pedido(s)</div>
                      <div><strong>{c.pages || 0}</strong> pág.</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h2 className="mb-3 flex items-center gap-2 text-lg font-black text-slate-900">
                <Palette className="h-5 w-5 text-[#4d7c0f]" /> B/N vs Color
              </h2>
              <div className="space-y-2">
                {(stats?.by_color || []).length === 0 && (
                  <div className="text-sm text-slate-500">Aún no hay datos de configuración.</div>
                )}
                {(stats?.by_color || []).map((c) => (
                  <div key={String(c._id || "sin-dato")} className="flex items-center justify-between rounded-lg bg-slate-50 p-3 text-sm">
                    <div className="font-black text-slate-900">
                      {c._id === "bw" ? "Blanco y negro" : c._id === "color" ? "Color" : "Sin configurar"}
                    </div>
                    <div className="text-right text-xs">
                      <div><strong>{c.orders}</strong> pedido(s)</div>
                      <div><strong>{c.pages || 0}</strong> pág.</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-8 overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="border-b border-slate-200 bg-slate-50 p-4">
              <h2 className="text-lg font-black text-slate-900">Últimos pedidos históricos</h2>
              <p className="text-xs text-slate-500">Resumen permanente para analítica y futuras exportaciones.</p>
            </div>

            <table className="w-full text-left text-sm">
              <thead className="bg-white text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="p-3">Código</th>
                  <th className="p-3">Cliente</th>
                  <th className="p-3">Fecha</th>
                  <th className="p-3">Páginas</th>
                  <th className="p-3">Tamaño</th>
                  <th className="p-3">Estado</th>
                  <th className="p-3">Recoger</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-slate-500">
                      Aún no hay histórico. Se irá creando con los próximos pedidos.
                    </td>
                  </tr>
                )}

                {orders.map((o) => (
                  <tr key={o.id || o.code} className="border-t border-slate-100">
                    <td className="p-3 font-mono font-black text-[#4d7c0f]">{o.code || "—"}</td>
                    <td className="p-3">
                      <div className="font-bold text-slate-900">{o.client_name || "—"}</div>
                      {o.client_email && <div className="text-xs text-slate-500">{o.client_email}</div>}
                    </td>
                    <td className="p-3 whitespace-nowrap text-xs text-slate-600">{formatDate(o.created_at)}</td>
                    <td className="p-3 font-bold">{o.page_count ?? "—"}</td>
                    <td className="p-3 text-xs">{formatBytes(o.total_size || 0)}</td>
                    <td className="p-3">
                      <Badge variant="outline">{o.status || "—"}</Badge>
                    </td>
                    <td className="p-3 text-xs">
                      {o.pickup_ready ? (
                        <span className="font-bold text-[#7c3aed]">
                          Sí {o.pickup_datetime ? `· ${o.pickup_datetime}` : ""}
                        </span>
                      ) : (
                        <span className="text-slate-400">No</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
